import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileSpreadsheet, Loader2, BarChart3 } from "lucide-react";

const fmtMin = (v) => (v == null ? "—" : v < 1 ? "أقل من دقيقة" : v < 60 ? `${Math.round(v)} دقيقة` : `${(v / 60).toFixed(1)} ساعة`);
const fmtRate = (v) => (v == null ? "—" : `${v} ★`);

function lastMonths(n) {
  const d = new Date();
  return Array.from({ length: n }, (_, i) => {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const value = `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
    return { value, label: x.toLocaleDateString("ar-SA-u-ca-gregory", { month: "long", year: "numeric" }) };
  });
}

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-muted/40 p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-bold mt-0.5" data-testid={testid}>{value}</div>
  </div>
);

export function SupportReport() {
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth] = useState(months[0].value);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRep(null);
    api.get("/admin/support-report", { params: { month } }).then((r) => setRep(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [month]);

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/support-report/export", { params: { month }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `support-report-${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("تعذر تنزيل التقرير");
    }
    setBusy(false);
  };

  const t = rep?.total;
  return (
    <Card className="p-5 space-y-4" data-testid="support-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /> تقرير أداء الدعم الشهري</h3>
        <div className="flex gap-2">
          <Select value={month} onValueChange={setMonth} dir="rtl">
            <SelectTrigger className="w-44" data-testid="report-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {months.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`report-month-${m.value}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={exportXlsx} disabled={busy} className="rounded-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="report-export-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} تنزيل Excel
          </Button>
        </div>
      </div>
      {!rep ? <div className="py-8 text-center text-sm text-muted-foreground">جارٍ التحميل...</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="التذاكر" value={t.tickets} testid="report-total-tickets" />
            <Stat label="المغلقة" value={t.closed} testid="report-total-closed" />
            <Stat label="متوسط أول رد" value={fmtMin(t.avg_first_response_min)} testid="report-total-first-response" />
            <Stat label="متوسط وقت الإغلاق" value={fmtMin(t.avg_close_min)} testid="report-total-close" />
            <Stat label="متوسط التقييم" value={fmtRate(t.avg_rating)} testid="report-total-rating" />
          </div>
          {rep.agents.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="report-empty">لا توجد تذاكر في هذا الشهر</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="text-muted-foreground text-xs">
                  <tr className="border-b border-border">
                    {["الموظف", "التذاكر", "المغلقة", "متوسط أول رد", "متوسط وقت الإغلاق", "متوسط التقييم"].map((h) => <th key={h} className="text-start py-2 px-2 font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rep.agents.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0" data-testid={`report-row-${a.id}`}>
                      <td className="py-2.5 px-2 font-semibold">{a.name}</td>
                      <td className="px-2">{a.tickets}</td>
                      <td className="px-2">{a.closed}</td>
                      <td className="px-2">{fmtMin(a.avg_first_response_min)}</td>
                      <td className="px-2">{fmtMin(a.avg_close_min)}</td>
                      <td className="px-2">{fmtRate(a.avg_rating)} {a.ratings > 0 && <span className="text-xs text-muted-foreground">({a.ratings})</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
